import React, { useCallback, useState, useContext } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { Typography, useMediaQuery } from '@material-ui/core';

import { history } from '../../../../Routes/history';
import BaseButton from '../../../../Components/Buttons/BaseButton';
import BaseDialog from '../../../../Components/Dialogs/BaseDialog';

import { SlideUpTransition } from '../../../../Components/Transitions/Transitions';
import { floatWithDots } from '../../../../Utils/Format/MoneyFormat';

import { OpenNewTab } from '../../../../Utils/Misc/Links';
import { ROUTE_NAMES } from '../../../../Routes/Routes';
import { saveAsPDF } from '../../../../Utils/Transform/Files';
import { GetCouponPDFAPI } from '../../../../API/Coupons/CouponsAPI';
import { AlertsDispatchContext } from '../../../../Contexts/AlertsContext';
import { Company } from '../../../../Configs/general';

const GenerationPartialPaymentStep = props => {
  const { step, onClose, open, title } = props;

  const classes = useStyles();

  // * STATES
  const [loading, setLoading] = useState(false);

  const data = step.data;

  // * CONTEXTS
  const setAlert = useContext(AlertsDispatchContext);

  const goToPayments = useCallback(
    event => {
      event.preventDefault();
      history.push(ROUTE_NAMES.payments, {
        type: 'coupon',
        value: data.couponId,
        currentStep: 0,
        disabledNext: false
      });
    },
    [data]
  );

  const _goToCloserMap = useCallback(() => {
    const baseUrl = window.location.origin;
    OpenNewTab(`${baseUrl}${ROUTE_NAMES.map}`);
  }, []);

  const onDownloadCoupon = useCallback(
    async event => {
      event.preventDefault();

      setLoading(true);
      const response = await GetCouponPDFAPI(data.couponId);
      if (!response.success) {
        setLoading(false);
        setAlert({
          type: 'error',
          message: 'Ocurrió un error, intenta de nuevo'
        });
        return;
      }
      const base64 = response.data.data.pdf;
      const pdfName = `pagos_${Company.shortName.toLowerCase()}_cupon_${
        data.couponId
      }.pdf`;
      saveAsPDF(base64, pdfName);
      setLoading(false);
    },
    [data.couponId, setAlert]
  );

  //* COMPONENTS
  const renderContent = useCallback(() => {
    return (
      <div id="GenerationStep_div_container" className={classes.content}>
        {
          <Typography className={classes.text}>
            Se ha generado el cupón #{data.couponId} para el abono de tu deuda
            por un valor de <strong>{floatWithDots(data.value)}</strong>.
          </Typography>
        }
        <div className={classes.optionsContainer}>
          <BaseButton
            variant="outlined"
            size="large"
            disabled={loading}
            onClick={onDownloadCoupon}
          >
            {loading ? 'Generando PDF' : 'Descargar PDF'}
          </BaseButton>
          <BaseButton
            size="large"
            onClick={goToPayments}
            className={classes.rightButton}
          >
            Pagar en línea
          </BaseButton>
        </div>
        <Typography className={classes.textSmall}>
          Puedes descargar el cupón y pagarlo en cualquiera de nuestros{' '}
          <span className={classes.linkTextSmall} onClick={_goToCloserMap}>
            canales de pago
          </span>
        </Typography>
      </div>
    );
  }, [data, goToPayments, _goToCloserMap, classes, loading, onDownloadCoupon]);

  const isMobileSize = useMediaQuery(theme =>
    theme.breakpoints.down(theme.breakpoints.values.sm)
  );
  return (
    <>
      <BaseDialog
        open={open}
        loading={loading}
        handleClose={onClose}
        title={title}
        actions={null}
        content={renderContent}
        fullScreen={isMobileSize}
        contentSize="small"
        TransitionComponent={isMobileSize ? SlideUpTransition : undefined}
        disableBackdropClick={true}
      />
    </>
  );
};

const useStyles = makeStyles(theme => ({
  content: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'space-around',
    minHeight: 200
  },
  valueText: {
    fontSize: 14,
    marginTop: theme.spacing(4)
  },
  text: {
    fontSize: 14,
    color: theme.palette.text.primary,
    marginBottom: theme.spacing(2),
    width: '100%'
  },
  title: {
    fontSize: 14,
    marginBottom: theme.spacing(2)
  },
  optionsContainer: {
    display: 'flex',
    width: '100%',
    flexDirection: 'row',
    justifyContent: 'space-around',
    marginTop: theme.spacing(),
    marginBottom: theme.spacing(2)
  },
  textSmall: {
    fontSize: 10,
    color: theme.palette.text.blackLight,
    marginTop: theme.spacing()
  },
  rightButton: {
    marginLeft: theme.spacing(3)
  },
  linkTextSmall: {
    cursor: 'pointer',
    textDecoration: 'underline',
    '&:hover': {
      color: theme.palette.primary.dark
    }
  }
}));

export default GenerationPartialPaymentStep;
